import { useEffect, useMemo } from "react"
import { create } from "zustand"
import { fetchAdminRows } from "@client/admin/api"
import type { AdminEntity, AdminRow } from "@client/admin/types"

type Rows = Partial<Record<AdminEntity, AdminRow[]>>
type Loaded = Record<AdminEntity, boolean>
type Loading = Record<AdminEntity, boolean>
type Errors = Partial<Record<AdminEntity, string>>

const ADMIN_ENTITIES: AdminEntity[] = [
  "inquiry",
  "application",
  "appNote",
  "subscriber",
  "newsletter",
  "delivery",
]

const emptyLoaded = (): Loaded =>
  Object.fromEntries(ADMIN_ENTITIES.map((entity) => [entity, false])) as Loaded

interface EntitiesState {
  rows: Rows
  loaded: Loaded
  loading: Loading
  errors: Errors
  loadEntity: (entity: AdminEntity, force?: boolean) => Promise<void>
  reloadEntity: (entity: AdminEntity) => Promise<void>
  applyIncoming: (entity: AdminEntity, rows: AdminRow[]) => void
  replaceAll: (entity: AdminEntity, rows: AdminRow[]) => void
  optimisticUpdate: (
    entity: AdminEntity,
    id: string,
    row: AdminRow,
    request: () => Promise<AdminRow>,
  ) => Promise<AdminRow>
  optimisticCreate: (
    entity: AdminEntity,
    row: AdminRow,
    request: () => Promise<AdminRow>,
  ) => Promise<AdminRow>
  optimisticRemove: (
    entity: AdminEntity,
    id: string,
    request: () => Promise<AdminRow>,
  ) => Promise<AdminRow>
  optimisticRemoveMany: (
    entity: AdminEntity,
    ids: readonly string[],
    request: () => Promise<AdminRow[]>,
  ) => Promise<AdminRow[]>
}

function mergeRows(current: AdminRow[], incoming: AdminRow[]): AdminRow[] {
  const merged = new Map(current.map((row) => [row.id, row]))
  for (const row of incoming) merged.set(row.id, row)
  return [...merged.values()]
}

const requests = new Map<AdminEntity, Promise<void>>()

export const useEntitiesStore = create<EntitiesState>((set, get) => ({
  rows: {},
  loaded: emptyLoaded(),
  loading: Object.fromEntries(ADMIN_ENTITIES.map((entity) => [entity, false])) as Loading,
  errors: {},

  loadEntity: (entity, force = false) => {
    if (!force && get().loaded[entity]) return Promise.resolve()
    const existing = requests.get(entity)
    if (existing) return existing

    set((state) => ({
      loading: { ...state.loading, [entity]: true },
      errors: { ...state.errors, [entity]: undefined },
    }))
    const request = fetchAdminRows(entity)
      .then((rows) => {
        set((state) => ({
          rows: { ...state.rows, [entity]: rows },
          loaded: { ...state.loaded, [entity]: true },
        }))
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : String(error)
        set((state) => ({ errors: { ...state.errors, [entity]: message } }))
        throw error
      })
      .finally(() => {
        requests.delete(entity)
        set((state) => ({ loading: { ...state.loading, [entity]: false } }))
      })
    requests.set(entity, request)
    return request
  },

  reloadEntity: (entity) => get().loadEntity(entity, true),

  applyIncoming: (entity, incoming) =>
    set((state) => ({
      rows: { ...state.rows, [entity]: mergeRows(state.rows[entity] ?? [], incoming) },
    })),

  replaceAll: (entity, rows) => set((state) => ({ rows: { ...state.rows, [entity]: rows } })),

  optimisticUpdate: async (entity, id, row, request) => {
    const previous = get().rows[entity] ?? []
    set((state) => ({ rows: { ...state.rows, [entity]: mergeRows(previous, [row]) } }))
    try {
      const saved = await request()
      get().applyIncoming(entity, [saved])
      await get().reloadEntity(entity).catch(() => {})
      return saved
    } catch (error) {
      get().replaceAll(entity, previous)
      throw error
    }
  },

  optimisticCreate: async (entity, row, request) => {
    const previous = get().rows[entity] ?? []
    set((state) => ({ rows: { ...state.rows, [entity]: mergeRows(previous, [row]) } }))
    try {
      const saved = await request()
      set((state) => ({
        rows: {
          ...state.rows,
          [entity]: mergeRows(
            (state.rows[entity] ?? []).filter((current) => current.id !== row.id),
            [saved],
          ),
        },
      }))
      await get().reloadEntity(entity).catch(() => {})
      return saved
    } catch (error) {
      get().replaceAll(entity, previous)
      throw error
    }
  },

  optimisticRemove: async (entity, id, request) => {
    const previous = get().rows[entity] ?? []
    set((state) => ({
      rows: { ...state.rows, [entity]: previous.filter((row) => row.id !== id) },
    }))
    try {
      const removed = await request()
      await get().reloadEntity(entity).catch(() => {})
      return removed
    } catch (error) {
      get().replaceAll(entity, previous)
      throw error
    }
  },

  optimisticRemoveMany: async (entity, ids, request) => {
    const previous = get().rows[entity] ?? []
    const idSet = new Set(ids)
    set((state) => ({
      rows: { ...state.rows, [entity]: previous.filter((row) => !idSet.has(row.id)) },
    }))
    try {
      const removed = await request()
      await get().reloadEntity(entity).catch(() => {})
      return removed
    } catch (error) {
      get().replaceAll(entity, previous)
      throw error
    }
  },
}))

export function useRows(entity: AdminEntity, enabled = true): {
  rows: AdminRow[] | null
  loading: boolean
  error: string | null
  retry: () => Promise<void>
} {
  const raw = useEntitiesStore((state) => state.rows[entity])
  const loading = useEntitiesStore((state) => state.loading[entity])
  const error = useEntitiesStore((state) => state.errors[entity] ?? null)
  const loadEntity = useEntitiesStore((state) => state.loadEntity)
  const reloadEntity = useEntitiesStore((state) => state.reloadEntity)

  useEffect(() => {
    if (enabled) void loadEntity(entity).catch(() => {})
  }, [enabled, entity, loadEntity])

  const rows = useMemo(() => (raw === undefined ? null : raw.filter((row) => !row.deletedAt)), [raw])
  return { rows, loading, error, retry: () => reloadEntity(entity) }
}
